/**
 * The purchase-trip wizard (P2-BR2, BR-13, P2-TR3).
 *
 * A trip to the haat is one event that produces several animals and several
 * costs, and recording it as separate entries afterwards is how the transport
 * money ends up attached to nothing. So it is one form and one submission.
 *
 * The allocation preview updates as the partner types. That is the whole
 * reason the wizard exists rather than a plain form: the split is arithmetic
 * four partners will check by hand the first time, and seeing it before
 * submitting is what makes it agreed rather than imposed.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAccounts, useCategories } from '../finance/api.js';
import { previewTrip } from './tripAllocation.js';
import { dobFromAgeMonths } from './animalAge.js';
import { useRecordTrip } from './api.js';

interface DraftAnimal {
  species: 'goat' | 'cattle';
  sex: 'male' | 'female';
  purchasePrice: string;
  /**
   * Age as the partner says it, converted to `dobEstimate` on submit.
   *
   * Optional in a way the price is not: the trip has to reconcile, so every
   * animal needs a price, but a seller who cannot say how old a goat is is
   * ordinary. A blank here sends no birth date at all rather than a guess.
   */
  ageMonths: string;
}

interface DraftCost {
  /** Matches a seeded expense category name; sent to the server, not shown. */
  label: string;
  /** What the partner actually reads. */
  labelKey: string;
  amount: string;
}

const EMPTY_ANIMAL: DraftAnimal = { species: 'goat', sex: 'female', purchasePrice: '', ageMonths: '' };

/** The three costs a haat trip always has, prefilled so nobody forgets one. */
const DEFAULT_COSTS: DraftCost[] = [
  // `label` stays English and is not for display: it has to match a seeded
  // category name so the cost can be filed, and it is what appears on the
  // P&L. `labelKey` is what the partner reads (NFR-6).
  { label: 'Transport', labelKey: 'trip.cost.transport', amount: '' },
  { label: 'Hasil', labelKey: 'trip.cost.hasil', amount: '' },
  { label: 'Dalal commission', labelKey: 'trip.cost.dalal', amount: '' },
];

export function TripWizard(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: accounts = [] } = useAccounts();
  /*
   * Trip costs have to name an expense category — the server requires one per
   * line so the money lands somewhere the P&L can group. The three default
   * labels match seeded category names exactly, so they are resolved by name
   * rather than asking a partner standing at a haat to pick from a list.
   */
  const { data: categories = [] } = useCategories();
  const recordTrip = useRecordTrip();

  const [purchaseDate, setPurchaseDate] = useState(todayInDhaka());
  const [animals, setAnimals] = useState<DraftAnimal[]>([{ ...EMPTY_ANIMAL }]);
  const [costs, setCosts] = useState<DraftCost[]>(DEFAULT_COSTS);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // The same allocator the server uses, so the preview cannot disagree with
  // what is actually written (P2-BR2).
  const preview = useMemo(() => previewTrip(animals, costs), [animals, costs]);

  const priced = animals.filter((animal) => animal.purchasePrice.trim() !== '');
  // BR-05 — a purchase names the account it came from. The server requires it,
  // so the button waits for it rather than failing after the partner taps save.
  const canSubmit =
    priced.length === animals.length && animals.length > 0 && accountId !== null && !recordTrip.isPending;

  const updateAnimal = (index: number, patch: Partial<DraftAnimal>): void => {
    setAnimals((current) => current.map((animal, i) => (i === index ? { ...animal, ...patch } : animal)));
  };

  async function handleSubmit(): Promise<void> {
    setErrorKey(null);

    try {
      const categoryIdFor = (label: string): number | undefined =>
        categories.find((category) => category.name.toLowerCase() === label.toLowerCase())?.id;

      await recordTrip.mutateAsync({
        // The server's field names, not local ones — see TripInput.
        date: purchaseDate,
        accountId: accountId!,
        ...(categoryIdFor('Animal Purchase') !== undefined
          ? { animalCategoryId: categoryIdFor('Animal Purchase')! }
          : {}),
        animals: animals.map((animal) => {
          // An unparseable or empty age sends no key at all. The server's
          // schema is .strict() and `dobEstimate: undefined` would still be a
          // key on the wire — the mismatch that already cost this wizard once.
          const dobEstimate = dobFromAgeMonths(animal.ageMonths);
          return {
            species: animal.species,
            sex: animal.sex,
            purchasePrice: animal.purchasePrice,
            ...(dobEstimate !== null ? { dobEstimate } : {}),
          };
        }),
        tripCosts: costs
          .filter((cost) => cost.amount.trim() !== '' && Number(cost.amount) > 0)
          // A cost whose category cannot be resolved is dropped rather than
          // sent without one, which the server would reject outright — losing
          // the whole trip over one line the partner cannot see or fix.
          .flatMap((cost) => {
            const categoryId = categoryIdFor(cost.label);
            return categoryId === undefined ? [] : [{ label: cost.label, amount: cost.amount, categoryId }];
          }),
      });

      void navigate('/animals');
    } catch (error) {
      // Nothing is cleared: the partner is standing at a haat with a paper
      // slip, and retyping five animals is how a trip goes unrecorded (NFR-2).
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <form
      className="page-container"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('trip.title')}</h1>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="purchaseDate">
        {t('trip.date')}
      </label>
      <input
        id="purchaseDate"
        type="date"
        value={purchaseDate}
        max={todayInDhaka()}
        onChange={(event) => setPurchaseDate(event.target.value)}
        className="mb-5 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />

      <fieldset className="mb-5">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('trip.animals')}</legend>

        <ul className="space-y-2">
          {animals.map((animal, index) => (
            <li key={index} className="rounded-xl bg-white p-3 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="w-6 shrink-0 text-sm text-gray-400">{index + 1}</span>

                <select
                  aria-label={t('trip.species')}
                  value={animal.species}
                  onChange={(event) => updateAnimal(index, { species: event.target.value as DraftAnimal['species'] })}
                  className="min-h-touch rounded-lg border border-gray-300 px-2 py-2 text-sm"
                >
                  <option value="goat">{t('animals.species.goat')}</option>
                  <option value="cattle">{t('animals.species.cattle')}</option>
                </select>

                <select
                  aria-label={t('trip.sex')}
                  value={animal.sex}
                  onChange={(event) => updateAnimal(index, { sex: event.target.value as DraftAnimal['sex'] })}
                  className="min-h-touch rounded-lg border border-gray-300 px-2 py-2 text-sm"
                >
                  <option value="female">{t('animals.sex.female')}</option>
                  <option value="male">{t('animals.sex.male')}</option>
                </select>

                {/* Narrow and before the price, because the price is the field
                    that has to be filled and should keep the wider box. */}
                <input
                  aria-label={t('trip.ageMonths')}
                  inputMode="numeric"
                  placeholder={t('trip.agePlaceholder')}
                  value={animal.ageMonths}
                  onChange={(event) => updateAnimal(index, { ageMonths: event.target.value })}
                  className="min-h-touch w-14 rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
                />

                <input
                  aria-label={t('trip.price', { n: index + 1 })}
                  inputMode="decimal"
                  placeholder="0"
                  value={animal.purchasePrice}
                  onChange={(event) => updateAnimal(index, { purchasePrice: event.target.value })}
                  className="min-h-touch w-24 flex-1 rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
                />

                {animals.length > 1 && (
                  <button
                    type="button"
                    aria-label={t('trip.removeAnimal', { n: index + 1 })}
                    onClick={() => setAnimals((current) => current.filter((_, i) => i !== index))}
                    className="min-h-touch min-w-touch text-gray-400"
                  >
                    ×
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => setAnimals((current) => [...current, { ...EMPTY_ANIMAL }])}
          className="mt-2 min-h-touch w-full rounded-lg border border-dashed border-gray-400 bg-white px-4 py-2 text-sm text-gray-700"
        >
          {t('trip.addAnimal')}
        </button>
      </fieldset>

      <fieldset className="mb-5">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('trip.costs')}</legend>

        <ul className="space-y-2">
          {costs.map((cost, index) => (
            <li key={cost.label} className="flex items-center gap-2">
              <span className="flex-1 text-sm text-gray-700">{t(cost.labelKey)}</span>
              <input
                aria-label={t(cost.labelKey)}
                inputMode="decimal"
                placeholder="0"
                value={cost.amount}
                onChange={(event) =>
                  setCosts((current) =>
                    current.map((row, i) => (i === index ? { ...row, amount: event.target.value } : row)),
                  )
                }
                className="min-h-touch w-28 rounded-lg border border-gray-300 px-2 py-2 text-right tabular-nums"
              />
            </li>
          ))}
        </ul>
      </fieldset>

      <fieldset className="mb-5">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('trip.paidFrom')}</legend>
        <div className="flex flex-wrap gap-2">
          {accounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => setAccountId(account.id)}
              aria-pressed={accountId === account.id}
              className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                accountId === account.id ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {account.name}
            </button>
          ))}
        </div>
      </fieldset>

      {/* The preview. This is what makes the split agreed rather than imposed. */}
      <section className="mb-5 rounded-xl bg-white p-4 shadow-sm" aria-label={t('trip.preview')}>
        <h2 className="mb-2 text-sm font-medium text-gray-700">{t('trip.preview')}</h2>

        <ul className="divide-y divide-gray-100">
          {preview.lines.map((line) => (
            <li key={line.label} className="flex items-baseline justify-between gap-2 py-2 text-sm">
              <span className="text-gray-600">{line.label}</span>
              <span className="text-xs text-gray-500">
                {formatMoney(line.purchasePrice, { symbol: false })} + {formatMoney(line.tripShare, { symbol: false })}
              </span>
              <span className="w-24 shrink-0 text-right tabular-nums font-medium text-gray-900">
                {formatMoney(line.totalCost)}
              </span>
            </li>
          ))}
        </ul>

        <dl className="mt-3 space-y-1 border-t border-gray-200 pt-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">{t('trip.animalsTotal')}</dt>
            <dd className="tabular-nums">{formatMoney(preview.animalsTotal)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">{t('trip.costsTotal')}</dt>
            <dd className="tabular-nums">{formatMoney(preview.tripCostTotal)}</dd>
          </div>
          <div className="flex justify-between font-medium">
            <dt>{t('trip.grandTotal')}</dt>
            <dd className="tabular-nums">{formatMoney(preview.grandTotal)}</dd>
          </div>
        </dl>
      </section>

      {errorKey !== null && (
        <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="min-h-[52px] w-full rounded-xl bg-green-700 text-lg font-medium text-white disabled:opacity-50"
      >
        {t(recordTrip.isPending ? 'trip.saving' : 'trip.submit')}
      </button>
    </form>
  );
}
